import { useEffect, useMemo, useRef, useState } from 'react';
import { List, Wrench, BookOpen, LifeBuoy, Share2, Settings, X } from 'lucide-react';

/**
 * TopActionsMenu — Mobile-only "More" menu for the 3D viewer.
 *
 * Replaces the Header + FloatingToolbar on mobile viewports.
 * Renders a floating trigger button (top-right) and an animated dropdown
 * with all viewer actions consolidated into a single menu.
 *
 * Accessibility: ARIA menu roles, keyboard navigation (Arrow keys, Home/End, Enter/Space, Escape).
 */

export interface TopActionsMenuProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (key: 'parts' | 'tools' | 'tutorial' | 'help' | 'share' | 'settings' | 'close') => void;
  menuId?: string;
}

interface MenuItem {
  key: TopActionsMenuProps['onSelect'] extends (key: infer K) => void ? K : never;
  label: string;
  Icon: typeof List;
}

export function TopActionsMenu({
  open,
  onOpenChange,
  onSelect,
  menuId = 'ac-more-menu',
}: TopActionsMenuProps) {
  const listRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);

  const items: MenuItem[] = useMemo(() => [
    { key: 'parts', label: 'Parts List', Icon: List },
    { key: 'tools', label: 'Tools List', Icon: Wrench },
    { key: 'tutorial', label: 'Tutorial', Icon: BookOpen },
    { key: 'help', label: 'Customer Service', Icon: LifeBuoy },
    { key: 'share', label: 'Share Project', Icon: Share2 },
    { key: 'settings', label: 'Settings', Icon: Settings },
    { key: 'close', label: 'Close Viewer', Icon: X },
  ], []);

  // Focus first item when menu opens
  useEffect(() => {
    if (open) {
      setActiveIndex(0);
      requestAnimationFrame(() => {
        itemRefs.current[0]?.focus();
      });
    }
  }, [open]);

  const close = () => onOpenChange(false);

  const handleSelect = (key: MenuItem['key']) => {
    onSelect(key);
    close();
  };

  const focusItem = (idx: number) => {
    const clamped = Math.max(0, Math.min(idx, items.length - 1));
    setActiveIndex(clamped);
    itemRefs.current[clamped]?.focus();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!open) return;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        focusItem((activeIndex + 1) % items.length);
        break;
      case 'ArrowUp':
        e.preventDefault();
        focusItem((activeIndex - 1 + items.length) % items.length);
        break;
      case 'Home':
        e.preventDefault();
        focusItem(0);
        break;
      case 'End':
        e.preventDefault();
        focusItem(items.length - 1);
        break;
      case 'Escape':
        e.preventDefault();
        close();
        break;
    }
  };

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={close}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 998,
          background: 'rgba(0, 0, 0, 0.4)',
          animation: 'acFadeIn 0.15s ease-out',
        }}
      />

      {/* Menu panel */}
      <div
        id={menuId}
        role="menu"
        aria-label="More options"
        ref={listRef}
        onKeyDown={onKeyDown}
        style={{
          position: 'fixed',
          top: 'max(0.75rem, env(safe-area-inset-top, 0.75rem))',
          right: '0.75rem',
          zIndex: 999,
          minWidth: '200px',
          padding: '0.5rem',
          borderRadius: '1rem',
          background: 'rgba(20, 20, 28, 0.95)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
          animation: 'acSlideIn 0.16s ease-out',
        }}
      >
        {items.map(({ key, label, Icon }, i) => (
          <button
            key={key}
            role="menuitem"
            tabIndex={activeIndex === i ? 0 : -1}
            ref={(el) => { itemRefs.current[i] = el; }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleSelect(key);
            }}
            onMouseEnter={() => setActiveIndex(i)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleSelect(key);
              }
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              width: '100%',
              padding: '0.75rem 1rem',
              borderRadius: '0.75rem',
              border: 'none',
              background: activeIndex === i ? 'rgba(255, 255, 255, 0.08)' : 'transparent',
              color: key === 'close' ? 'rgba(244, 63, 94, 0.9)' : 'rgba(255, 255, 255, 0.9)',
              fontSize: '0.875rem',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'background 0.15s',
              textAlign: 'left',
            }}
          >
            <Icon size={18} strokeWidth={2} style={{ opacity: 0.8, flexShrink: 0 }} />
            <span>{label}</span>
          </button>
        ))}
      </div>

      {/* Keyframe animations (injected once) */}
      <style>{`
        @keyframes acFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes acSlideIn {
          from { opacity: 0; transform: scale(0.96) translateY(-4px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </>
  );
}

export default TopActionsMenu;
